import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { Gb } from "@/components/l10n/Gb.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableBillingAccountReadOnly } from "@/components/table/DetailsTableBillingAccount.tsx";
import { MonthlyCostRow } from "@/components/table/DetailsTableMonthlyCost.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { useConfig } from "@/config.ts";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { getResourceTypeName } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation, useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { isMobileDevice } from "@/modules/main/mobile/isMobile.ts";
import { S3ServerUrl } from "@/modules/storage/account/StorageAccountView.tsx";
import { SuspendedNotice } from "@/modules/storage/account/SuspendedNotice.tsx";
import {
    getStorageAccountKeysQuery,
    type StorageAccountWithType,
} from "@/modules/storage/account/storageAccountApi.ts";
import { storageAccountListQueryWithPricesAtom } from "@/modules/storage/account/storageAccountQuery.ts";
import { changeBucketMutation, deleteBucketMutation, getS3ApiUrlQuery } from "@/modules/storage/bucket/bucketApi.ts";
import { t_billingAccountOverrideNotice } from "@/modules/storage/bucket/bucketMessages.tsx";
import { bucketListQueryWithPricesAtom } from "@/modules/storage/bucket/bucketQuery.ts";
import { storageAccountLink } from "@/modules/storage/links.ts";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import type { StorageAccountKey } from "@warrenio/api-spec/spec.oats.gen";
import React from "react";
import { first } from "remeda";

const S3FileBrowser = React.lazy(() => import("@/modules/storage/s3/S3FileBrowser.tsx"));

export function BucketView({ storageName }: { storageName: string }) {
    //#region Hooks
    const { t } = useLingui();
    const deleteMutation = useStandardMutation(deleteBucketMutation);
    const changeMutation = useStandardMutation(changeBucketMutation);

    const data = useSuspenseQueryAtom(bucketListQueryWithPricesAtom);
    const accounts = useSuspenseQueryAtom(storageAccountListQueryWithPricesAtom);

    const obj = useDeletableResource(() => getResourceById(data, storageName, "bucket"), deleteMutation);
    //#endregion

    if (obj === undefined) {
        return <NavigateAfterDelete />;
    }

    const {
        name,
        $type,
        num_objects,
        created_at,
        billing_account_id,
        billing_account_id_override,
        size_bytes,
        price,
        storage_account_uuid,
    } = obj;

    const account = getResourceById(accounts, storage_account_uuid, "storage_account");

    const { is_suspended } = account;

    async function onDelete() {
        await deleteMutation.mutateAsync({ name });
    }

    async function clearBillingAccount() {
        await changeMutation.mutateAsync({
            body: { name, billing_account_id: undefined, storage_account_uuid },
        });
    }

    const migrateBlock = (
        <NoticeBlock
            icon="jp-warning-icon"
            button={
                <WButton color="primary" size="bar" icon="i-lucide:rotate-ccw" action={clearBillingAccount}>
                    <Trans comment="Button: Remove the over-ride of the billing account for this bucket">
                        Clear override
                    </Trans>
                </WButton>
            }
        >
            {t_billingAccountOverrideNotice}
        </NoticeBlock>
    );

    return (
        <>
            <Toolbar>
                <WButton
                    color="muted"
                    size="bar"
                    variant="ghost"
                    action={storageAccountLink({ uuid: storage_account_uuid } as StorageAccountWithType)}
                    icon="jp-icon-caretleft"
                >
                    <Trans>Storage account</Trans>
                </WButton>

                <DeleteModal modalAction={async () => await onDelete()}>
                    <Trans>
                        Deleting the storage bucket "{name}" will permanently remove it, and any files in it will not be
                        accessible after that.
                    </Trans>
                </DeleteModal>
            </Toolbar>

            <ContentPane className="VStack gap-4">
                {billing_account_id_override && migrateBlock}

                <ViewTitle title={name} />

                <DetailsHolder>
                    <DetailsTable>
                        <DetailsTableRow title={t`Name`}>
                            <ClipBoardTooltip>{name}</ClipBoardTooltip>
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Type`}>
                            <ResourceWithIcon type={$type} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`Size`}>
                            <Gb bytes={size_bytes} />
                        </DetailsTableRow>
                        <DetailsTableRow title={t`No. of files`}>{num_objects}</DetailsTableRow>

                        <DetailsTableRow title={getResourceTypeName("storage_account")}>
                            {account.title}
                        </DetailsTableRow>
                    </DetailsTable>

                    <Spacer />

                    <DetailsTable>
                        <DetailsTableRow title={t`Server`}>
                            <S3ServerUrl />
                        </DetailsTableRow>

                        <DetailsTableRow title={t`Created`}>
                            <LongDate date={created_at} />
                        </DetailsTableRow>

                        <DetailsTableBillingAccountReadOnly billingAccountId={billing_account_id} />
                        <MonthlyCostRow price={price} billingAccountId={billing_account_id} />
                    </DetailsTable>
                </DetailsHolder>
            </ContentPane>

            <Separator />

            <ContentPane className="VStack gap-4">
                <SectionHeader title={<Trans>Files</Trans>} />

                {is_suspended ? (
                    <SuspendedNotice billing_account_id={billing_account_id} />
                ) : (
                    <ContentLoadingSuspense>
                        <FilesContent bucketName={name} storageAccountUuid={storage_account_uuid} />
                    </ContentLoadingSuspense>
                )}
            </ContentPane>
        </>
    );
}

function getFileBrowserAccessKey(storageKeys: StorageAccountKey[]): StorageAccountKey | undefined {
    // Prefer a full-access key so file operations work, but retain read-only browsing when no such key exists.
    return storageKeys.find((key) => !key.read_only) ?? first(storageKeys);
}

function FilesContent({ bucketName, storageAccountUuid }: { bucketName: string; storageAccountUuid: string }) {
    const storageKeys = useStandardSuspenseQuery(getStorageAccountKeysQuery, storageAccountUuid);

    const {
        data: { url: s3url },
    } = useStandardSuspenseQuery(getS3ApiUrlQuery);

    const { s3ForcePathStyle } = useConfig();

    const firstAccess = getFileBrowserAccessKey(storageKeys.data);

    return !firstAccess ? (
        <p className="color-muted">
            <Trans>You need to have at least one access key for browsing files in this bucket.</Trans>
        </p>
    ) : (
        <S3FileBrowser
            clientOptions={{
                accessKeyId: firstAccess.access_key,
                secretAccessKey: firstAccess.secret_key,
                region: "us-east-1", // Dummy region
                endpoint: s3url,
                forcePathStyle: s3ForcePathStyle,
            }}
            bucketName={bucketName}
            mobileView={isMobileDevice}
        />
    );
}
